import { distanceInWordsToNow } from 'date-fns';
import { useRouter } from 'found';
import { get, has } from 'lodash';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningIcon from '@mui/icons-material/Warning';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import LoadBlur from 'components/LoadBlur';
import { ListItemAvatar, ListItemSecondaryAction } from '@mui/material';
import { convertToLocalDate } from '../../utils/helper';

// const activityTypeSlugs = [
//   'chatterbox-selection',
//   'house-party-selection',
//   'activity-completion-reminder',
// ];

const Notification = ({
  noBackgroundColor,
  notification,
  showRightIcon,
  viewer,
  handleRead,
}) => {
  const { entityModel, entityId } = notification;

  const theme = useTheme();
  const styles = {
    unread: {
      borderLeft: `5px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.light,
    },
    urgent: {
      color: '#F99106',
    },
  };
  const { router } = useRouter();
  const [saving, setSaving] = useState(false);

  const handleClick = useCallback(() => {
    if (!notification.isViewed) {
      setSaving(true);
      handleRead({
        variables: {
          isViewed: true,
          notificationId: Number(notification.id),
        },
      }).then(() => {
        setSaving(false);
      });
    }
    if (entityModel.toLowerCase() === 'discussion') {
      router.push(`/comments/${entityId}`);
    } else if (entityModel.toLowerCase() === 'socialasset') {
      router.push(`/media/${entityId}`);
    } else if (entityModel.toLowerCase() === 'review') {
      router.push(`/reviews/${entityId}`);
    }
    // else if (eventEntity) {
    //   if (activityTypeSlugs.includes(notification.type.slug)) {
    //     router.push(`/events/${eventEntity.slug}/activities`);
    //   } else {
    //     router.push(`/events/${eventEntity.slug}`);
    //   }
    // } else if (mediumEntity) {
    //   router.push(`/media/${mediumEntity.id}`);
    // } else if (partyEntity) {
    //   router.push(`/parties/${partyEntity.id}`);
    // } else if (reviewEntity) {
    //   router.push(`/reviews/${reviewEntity.id}`);
    // }
  }, [notification, router, setSaving, viewer]);

  const personDisplayName = useMemo(
    () => get(notification, 'first', 'Someone'),
    [notification],
  );

  const eventDisplayName = useMemo(
    () => (has(notification, 'event.name') ? notification.event.name : 'event'),
    [notification],
  );

  const primaryText = useMemo(() => {
    let label = 'content';
    if (entityModel === 'Discussion') {
      label = 'comment';
    } else if (entityModel === 'SocialAsset') {
      label = 'media';
    } else if (entityModel === 'Review') {
      label = 'review';
    }
    if (notification.slug) {
      switch (notification.slug) {
        case 'host-notified-when-guest-rsvps':
          return `${personDisplayName} RSVP'd to your party`;
        case 'author-notified-when-post-commented-on':
          return `${personDisplayName} commented on your post`;
        case 'author-notified-when-media-commented-on':
          return `${personDisplayName} commented on your media`;
        case 'author-notified-when-review-commented-on':
          return `${personDisplayName} commented on your review`;
        case 'author-notified-when-discussion-liked':
          return `${personDisplayName} liked your comment`;
        case 'author-notified-when-media-liked':
          return `${personDisplayName} liked your media`;
        case 'author-notified-when-review-liked':
          return `${personDisplayName} liked your review`;
        case 'author-notified-when-post-shared':
          return `${personDisplayName} shared your post`;
        case 'author-notified-when-media-shared':
          return `${personDisplayName} shared your media`;
        case 'author-notified-when-review-shared':
          return `${personDisplayName} shared your review`;
        case 'activity-completion-reminder':
          return `Time is running out to complete your ${eventDisplayName} activities!`;
        case 'house-party-selection':
          return `Set up your ${eventDisplayName} party now!`;
        case 'chatterbox-selection':
          return `You are a ${eventDisplayName} Chatterbox!`;
        case 'moderation-indecency':
          return `Your ${label} has been moderated as “Indecent”`;
        case 'moderation-underage':
          return `Your ${label} has been moderated as “Underage”`;
        case 'moderation-irrelevant':
          return `Your ${label} has been moderated as “Irrelevant”`;
        case 'moderation-unbranded':
          return `Your ${label} has been moderated as “Irrelevant Party Photo”`;
        default:
          return 'Notice';
      }
    }
    return 'Notice';
  }, [entityModel, eventDisplayName, notification.type, personDisplayName]);
  const leftAvatar = useMemo(
    () => (has(notification, 'person.headshot.url') ? (
      <Avatar src={notification.person.headshot.url} />
      ) : null),
    [notification],
  );
  const leftIcon = useMemo(
    () => (notification.urgent ? <WarningIcon color='#f99106' /> : null),
    [notification.urgent],
  );
  const rightIcon = useMemo(
    () => (showRightIcon ? <ChevronRightIcon /> : null),
    [showRightIcon],
  );
  const secondaryText = useMemo(
    () => `${distanceInWordsToNow(convertToLocalDate(notification.created))} ago`,
    [notification.created],
  );
  const style = useMemo(
    () => (!notification.isViewed && !noBackgroundColor ? styles.unread : {}),
    [noBackgroundColor, notification.isViewed],
  );
  return (
    <LoadBlur loading={saving} size={30} thickness={4}>
      <ListItem onClick={handleClick} style={style} button>
        {leftAvatar && <ListItemAvatar>{leftAvatar}</ListItemAvatar>}
        {leftIcon && <ListItemIcon>{leftIcon}</ListItemIcon>}
        <ListItemText primary={primaryText} secondary={secondaryText} />
        <ListItemSecondaryAction>{rightIcon}</ListItemSecondaryAction>
      </ListItem>
    </LoadBlur>
  );
};

Notification.propTypes = {
  noBackgroundColor: PropTypes.bool,
  notification: PropTypes.object.isRequired,
  showRightIcon: PropTypes.bool,
  viewer: PropTypes.object.isRequired,
  handleRead: PropTypes.func.isRequired,
};

export default Notification;
